/**
 * TripListOverlay - Full-page passport card carousel for browsing trips.
 *
 * Displays trips as passport-styled cards in a horizontal carousel.
 * Positioned at the bottom of the screen over the globe background.
 * Includes navigation arrows that appear on hover.
 */

import { memo, useState } from 'react'
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react'
import { PassportCard } from '@/components/ui/PassportCard'
import { HorizontalScrollCarousel } from '@/components/ui/HorizontalScrollCarousel'
import { PaginationDots } from '@/components/ui/PaginationDots'
import { useCarouselNavigation } from './useCarouselNavigation'
import { FONT_STYLES } from '@/styles/fonts'
import type { TripRow } from './types'
import type { Trip as TripMessage } from '@generated/proto/model'

interface TripListOverlayProps {
  /** List of trips to display */
  trips: TripRow[]
  /** Loading state */
  loading?: boolean
  /** Called when a trip is selected */
  onSelectTrip: (tripId: string, trip: TripMessage) => void
  /** Called when create button is clicked, optionally with a location from globe click */
  onCreateTrip: (location?: { latitude: number; longitude: number }) => void
}

/**
 * Trip list overlay component with passport card carousel.
 *
 * @example
 * ```tsx
 * <TripListOverlay
 *   trips={trips}
 *   onSelectTrip={(id, trip) => selectTrip(id)}
 *   onCreateTrip={() => setShowCreateDialog(true)}
 * />
 * ```
 */
export const TripListOverlay = memo(function TripListOverlay({
  trips,
  loading = false,
  onSelectTrip,
  onCreateTrip,
}: TripListOverlayProps) {
  // Carousel navigation and globe sync
  const {
    carouselRef,
    centeredIndex,
    handleCenterChange,
    handlePrev,
    handleNext,
    handleDotClick,
  } = useCarouselNavigation({ trips, loading })

  // Track which card is being hovered for pagination dot highlight
  const [hoveredIndex, setHoveredIndex] = useState(-1)

  return (
    <div className="absolute bottom-[12%] left-0 right-0 z-50 group/carousel">
      {/* Carousel Container - extra padding for hover scale, pb-8 for pagination dots */}
      <div className="relative pt-16 pb-8">
        {/* Navigation Arrows - only visible on hover when there are enough cards */}
        {trips.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white/70 hover:text-white hover:bg-black/70 transition-all duration-200 opacity-0 group-hover/carousel:opacity-100"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white/70 hover:text-white hover:bg-black/70 transition-all duration-200 opacity-0 group-hover/carousel:opacity-100"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
          </div>
        ) : (
          <>
            {/* Horizontal Scroll Container */}
            <HorizontalScrollCarousel
              ref={carouselRef}
              onCenterChange={handleCenterChange}
              className="gap-8 py-[5%] px-[10vw]"
              style={{ scrollPaddingInline: '10vw' }}
            >
              {/* Add New Trip Card */}
              <button
                type="button"
                onClick={() => onCreateTrip()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onCreateTrip()
                  }
                }}
                onMouseEnter={() => setHoveredIndex(0)}
                onMouseLeave={() => setHoveredIndex(-1)}
                aria-label="Create a new trip"
                className="flex-shrink-0 snap-start cursor-pointer group text-left"
              >
                <div className="relative w-[280px] h-[392px] rounded-lg shadow-lg border border-white/50 overflow-hidden transition-all duration-300 origin-bottom group-hover:scale-[1.15] group-hover:shadow-2xl backdrop-blur-sm bg-black/50 flex items-center justify-center">
                  <div className="text-center">
                    <Plus
                      className="w-16 h-16 mx-auto mb-4 text-white/40 group-hover:text-white/60 transition-colors"
                      strokeWidth={1.5}
                    />
                    <p
                      className="text-white/50 group-hover:text-white/70 transition-colors uppercase tracking-widest text-sm"
                      style={FONT_STYLES.pioneer}
                    >
                      New Trip
                    </p>
                  </div>
                </div>
              </button>

              {/* Passport Cards */}
              {trips.map((trip, index) => (
                <PassportCard
                  key={trip.id}
                  trip={trip.proto}
                  tripId={trip.id}
                  onClick={() => onSelectTrip(trip.id, trip.proto)}
                  onMouseEnter={() => setHoveredIndex(index + 1)}
                  onMouseLeave={() => setHoveredIndex(-1)}
                />
              ))}
            </HorizontalScrollCarousel>

            {/* Pagination dots - absolutely positioned so they don't affect card centering */}
            {trips.length > 0 && (
              <div className="absolute left-0 right-0 bottom-4">
                <PaginationDots
                  total={trips.length + 1} // +1 for the "New Trip" card
                  current={centeredIndex}
                  hoveredIndex={hoveredIndex}
                  onDotClick={handleDotClick}
                />
              </div>
            )}

            {/* Empty state message - also absolutely positioned */}
            {trips.length === 0 && (
              <p
                className="absolute left-0 right-0 bottom-0 text-white/70 text-center drop-shadow-lg"
                style={FONT_STYLES.alga}
              >
                Click the + card to create your first trip!
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
})
